class NewTokenService {
  private readonly TOKEN_EXPIRY_KEY = "new_auth:tokenExpiry";
  private readonly REFRESH_TOKEN_KEY = "new_auth:refreshToken";
  private readonly REFRESH_BUFFER_SECONDS = 120;

  private accessToken: string | null = null;

  /**
   * Quem depende do TOKEN (e não do usuário) precisa saber quando ele chega.
   * O access token só vive em memória: no reload a página monta com `null` e o
   * token só aparece quando o refresh volta. Sem aviso, quem leu no render
   * ficou com a leitura velha — foi assim que o banner de personificação
   * sumiu depois de reidratar (G14).
   */
  private listeners = new Set<() => void>();

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  private isDev(): boolean {
    return import.meta.env.DEV;
  }

  setTokens(token: string, expiresAt?: number): void {
    const expiryTime = expiresAt || Date.now() + 60 * 60 * 1000;

    this.accessToken = token;
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
    this.notify();
  }

  setRefreshToken(refreshToken: string): void {
    if (this.isDev()) {
      sessionStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
  }

  getRefreshToken(): string | null {
    if (this.isDev()) {
      return sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    return null;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Lê o claim `impersonatorId` do access token in-memory (personificação).
   * Fonte autoritativa do estado "vendo como" — sobrevive a reload/aba nova
   * porque o /auth/refresh propaga o claim. Retorna null em sessão normal.
   */
  getImpersonatorId(): number | null {
    const token = this.accessToken;
    if (!token) return null;
    try {
      const part = token.split(".")[1] ?? "";
      const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
      const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      return typeof payload?.impersonatorId === "number"
        ? payload.impersonatorId
        : null;
    } catch {
      return null;
    }
  }

  getTokenExpiry(): number {
    const expiry = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : 0;
  }

  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (expiry === 0) return true;
    return Date.now() >= expiry;
  }

  isTokenExpiringSoon(seconds: number = this.REFRESH_BUFFER_SECONDS): boolean {
    const expiry = this.getTokenExpiry();
    if (expiry === 0) return true;
    return expiry - Date.now() <= seconds * 1000;
  }

  getTokenTimeLeft(): number {
    const expiry = this.getTokenExpiry();
    if (expiry === 0) return 0;
    return Math.max(0, Math.floor((expiry - Date.now()) / 1000));
  }

  clearTokens(): void {
    this.accessToken = null;
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this.notify();
  }

  hasValidToken(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }
}

export default new NewTokenService();
